import { Link } from "react-router-dom";

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

const footerLinks: { title: string; links: FooterLink[] }[] = [
  {
    title: "Capabilities",
    links: [
      { label: "Infrastructure & Network", href: "/capabilities/infrastructure-network-engineering" },
      { label: "Database & Data Platform", href: "/capabilities/database-data-platform" },
      { label: "Application & Platform", href: "/capabilities/application-platform-engineering" },
      { label: "Microsoft Solutions", href: "/capabilities/microsoft-solutions" },
      { label: "Web & Digital", href: "/capabilities/web-digital-solutions" },
      { label: "Mobile Application Development", href: "/capabilities/mobile-application-development" },
      { label: "AI & Intelligent Engineering", href: "/capabilities/ai-intelligent-engineering" },
    ],
  },
  {
    title: "Experience",
    links: [
      { label: "Experience Overview", href: "/experience" },
      { label: "USA Government Experience", href: "/experience/usa-government" },
      { label: "International Government Experience", href: "/experience/international" },
      { label: "Commercial Experience", href: "/experience/commercial" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Mitaja", href: "/about" },
      { label: "Careers", href: "/about/careers" },
      { label: "Markets", href: "/markets" },
      { label: "Resources", href: "/resources" },
      { label: "Technologies", href: "/technologies" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Employees",
    links: [
      { label: "TimeSheet Login", href: "https://us-timesheet-management.vercel.app/login", external: true },
      { label: "Paychex", href: "https://www.paychex.com/", external: true },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms & Website Notice", href: "/terms-website-notice" },
      { label: "Accessibility Statement", href: "/accessibility-statement" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="bg-primary-950 text-foreground-200">
      <div className="container-page section-padding-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <span className="text-white font-semibold text-lg tracking-tight">Mitaja</span>
            </div>
            <p className="text-sm text-foreground-400 leading-relaxed max-w-xs">
              Enterprise infrastructure, network engineering, database services, and specialized technical professionals for government and commercial organizations.
            </p>
          </div>

          {footerLinks.map((group) => (
            <div key={group.title}>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                {group.title}
              </h4>
              <ul className="space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-sm text-foreground-400 hover:text-white transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-foreground-400 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} Mitaja Corporation. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/contact" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors">
              Contact
            </Link>
            <Link to="/resources" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors">
              Resources
            </Link>
            <Link to="/about/careers" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors">
              Careers
            </Link>
            <Link to="/privacy-policy" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-website-notice" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors">
              Terms &amp; Website Notice
            </Link>
            <Link to="/accessibility-statement" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors">
              Accessibility Statement
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}